import axios from "axios";
import { useCallback, useEffect, useState } from "react";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { useNavigate } from "react-router-dom";

import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Divider,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";

// 設定項目で使用するアイコン
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import EngineeringIcon from "@mui/icons-material/Engineering";

import "../css/style.css";

import Header from "./Header";
import Loading from "../component/Loading";
import MaintenanceSetting from "../component/MaintenanceSetting";

import ConstList from "../utils/ConstList";
import { maintenanceState } from "../stores/MaintenanceAtom";
import { LoginUserAtom } from "../stores/LoginUserAtom";

/**
 * 管理者として扱う権限ID。
 *
 * master_authorityテーブルの管理者が
 * authority_id = 1のため、1を管理者として扱う。
 */
const ADMIN_AUTHORITY_ID = 1;

/**
 * GetMyProfile.phpから返されるユーザー情報の型。
 *
 * LoginUserTypeへ権限情報を追加せず、
 * この設定画面内だけでAPI応答の型を定義する。
 */
type ProfileResponse = {
  data?: {
    user_id?: number | string;
    email?: string;
    name?: string;
    authority_id?: number | string;
    authority_name?: string;
  } | null;
};

/**
 * システム設定画面。
 *
 * この画面では次の項目を扱う。
 *
 * ・ログインユーザーの権限確認
 * ・管理者専用のユーザー管理への入口
 * ・メンテナンス設定
 *
 * LoginUserAtomにはAuth0の基本情報だけを保持し、
 * DB上の権限はGetMyProfile.phpから別途取得する。
 */
const Setting = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  /**
   * LoginUserAtomからAuth0のログイン情報を取得する。
   *
   * このAtomには次の情報だけが保存されている。
   *
   * ・userId
   * ・email
   * ・userName
   * ・accessToken
   * ・algorithm
   *
   * 権限情報は含まれていないため、
   * emailを使ってGetMyProfile.phpから取得する。
   */
  const loginUserData = useRecoilValue(LoginUserAtom);

  /**
   * メンテナンス情報を保存するRecoil Atom。
   *
   * 取得した情報はHeaderやMaintenanceSettingからも
   * 利用できるようにする。
   */
  const setMaintenance = useSetRecoilState(maintenanceState);

  /**
   * メンテナンス情報の取得状態。
   */
  const [maintenanceLoading, setMaintenanceLoading] = useState(true);

  /**
   * メンテナンス情報の取得エラー。
   *
   * nullの場合はエラーなし。
   */
  const [maintenanceError, setMaintenanceError] = useState<string | null>(null);

  /**
   * DBから取得したログインユーザーの権限ID。
   *
   * null：
   *   未取得または取得失敗
   *
   * 1：
   *   管理者
   */
  const [authorityId, setAuthorityId] = useState<number | null>(null);

  /**
   * DBから取得した権限名。
   *
   * 例：
   * ・管理者
   * ・一般
   */
  const [authorityName, setAuthorityName] = useState("");

  /**
   * 権限情報の取得状態。
   */
  const [authorityLoading, setAuthorityLoading] = useState(true);

  /**
   * 権限情報の取得エラー。
   *
   * 権限を取得できなかった場合は、
   * ユーザー管理カードを表示しない。
   */
  const [authorityError, setAuthorityError] = useState<string | null>(null);

  /**
   * authority_idが1の場合だけ管理者として扱う。
   *
   * trueの場合：
   *   ユーザー管理カードを表示する。
   *
   * falseの場合：
   *   ユーザー管理カードを表示しない。
   */
  const isAdmin = authorityId === ADMIN_AUTHORITY_ID;

  /**
   * メンテナンス情報を取得する。
   *
   * 初回表示時と再読み込みボタン押下時に実行する。
   */
  const getMaintenance = useCallback(async () => {
    try {
      setMaintenanceLoading(true);
      setMaintenanceError(null);

      /**
       * 現在のフロントエンドバージョンを確認する。
       */
      ConstList.versionCheck();

      /**
       * PHP APIから現在のメンテナンス情報を取得する。
       */
      const response = await axios.get(
        ConstList.getApiUrl() + "GetMaintenance.php",
        ConstList.AXIOS_CONFIG,
      );

      /**
       * 取得した情報をRecoilへ保存する。
       */
      setMaintenance(response.data);
    } catch (caughtError) {
      console.error("メンテナンス情報の取得に失敗しました。", caughtError);

      setMaintenanceError(
        "メンテナンス情報の取得に失敗しました。通信状況を確認して再読み込みしてください。",
      );
    } finally {
      setMaintenanceLoading(false);
    }
  }, [setMaintenance]);

  /**
   * ログインユーザーの権限を取得する。
   *
   * LoginUserAtomに保存されているメールアドレスを
   * GetMyProfile.phpへ送信し、DB上の権限を取得する。
   */
  const getLoginUserAuthority = useCallback(async () => {
    const email = loginUserData.email?.trim() ?? "";

    /**
     * メールアドレスがまだAtomへ保存されていない場合は、
     * APIを呼ばずに待機する。
     */
    if (!email) {
      setAuthorityLoading(true);
      setAuthorityId(null);
      setAuthorityName("");
      setAuthorityError(null);
      return;
    }

    try {
      setAuthorityLoading(true);
      setAuthorityError(null);

      /**
       * GetMyProfile.phpへメールアドレスを送信する。
       *
       * PHP側ではこのメールアドレスを使って、
       * master_userとmaster_authorityを検索する。
       */
      const response = await axios.post<ProfileResponse>(
        ConstList.getApiUrl() + "GetMyProfile.php",
        {
          email,
        },
        ConstList.AXIOS_CONFIG,
      );

      const profile = response.data?.data;

      /**
       * ユーザー情報が返されなかった場合は、
       * 管理者として扱わない。
       */
      if (!profile) {
        setAuthorityId(null);
        setAuthorityName("");

        setAuthorityError("ログインユーザーがシステムに登録されていません。");

        return;
      }

      /**
       * PostgreSQLから返る数値は文字列になる場合があるため、
       * Numberで数値へ変換する。
       */
      const nextAuthorityId = Number(profile.authority_id);

      /**
       * authority_idが正常な数値でない場合は、
       * 管理者として扱わない。
       */
      if (!Number.isFinite(nextAuthorityId)) {
        setAuthorityId(null);
        setAuthorityName("");

        setAuthorityError("ログインユーザーの権限情報が正しくありません。");

        return;
      }

      /**
       * 取得した権限をSetting.tsx内のstateへ保存する。
       *
       * LoginUserTypeやLoginUserAtomは変更しない。
       */
      setAuthorityId(nextAuthorityId);

      setAuthorityName(profile.authority_name ?? "");
    } catch (caughtError) {
      /**
       * Axiosエラーの場合は、HTTPステータスや
       * APIから返された内容をコンソールへ出力する。
       *
       * accessToken全体はコンソールへ出力しない。
       */
      if (axios.isAxiosError(caughtError)) {
        console.error("ログインユーザーの権限取得に失敗しました。", {
          requestUrl: ConstList.getApiUrl() + "GetMyProfile.php",
          requestEmail: email,
          status: caughtError.response?.status,
          responseData: caughtError.response?.data,
          message: caughtError.message,
        });
      } else {
        console.error(
          "ログインユーザーの権限取得に失敗しました。",
          caughtError,
        );
      }

      setAuthorityId(null);
      setAuthorityName("");

      setAuthorityError("ログインユーザーの権限情報を取得できませんでした。");
    } finally {
      setAuthorityLoading(false);
    }
  }, [loginUserData.email]);

  /**
   * 設定画面を開いたときに、
   * メンテナンス情報を取得する。
   */
  useEffect(() => {
    void getMaintenance();
  }, [getMaintenance]);

  /**
   * LoginUserAtomへメールアドレスが保存された後、
   * ログインユーザーの権限情報を取得する。
   *
   * メールアドレスが後から設定された場合も、
   * 変更を検知して自動的に再実行される。
   */
  useEffect(() => {
    void getLoginUserAuthority();
  }, [getLoginUserAuthority]);

  /**
   * メンテナンス情報と権限情報を再取得する。
   *
   * ページ上部の「再読み込み」ボタンから実行する。
   */
  const handleReload = () => {
    void Promise.all([getMaintenance(), getLoginUserAuthority()]);
  };

  /**
   * ユーザー管理ページへ移動する。
   *
   * このボタンは管理者にだけ表示される。
   *
   * 遷移先：
   * /setting/users
   */
  const handleOpenUserManagement = () => {
    navigate("/setting/users");
  };

  /**
   * メンテナンスまたは権限のどちらかを取得中の場合、
   * 再読み込みボタンを無効化する。
   */
  const isReloading = maintenanceLoading || authorityLoading;

  /**
   * 画面上部に表示する権限状態。
   */
  const authorityStatusLabel = authorityLoading
    ? "権限確認中"
    : authorityError
      ? "権限取得失敗"
      : authorityName
        ? `権限：${authorityName}`
        : "権限なし";

  return (
    <>
      {/* ログイン後の共通ヘッダー */}
      <Header />

      <Box
        component="main"
        sx={{
          minHeight: "100vh",
          pt: {
            xs: 9,
            md: 10,
          },
          pb: 6,

          /**
           * テーマに合わせて、
           * ページ上部へ薄いグラデーションを表示する。
           */
          background:
            theme.palette.mode === "dark"
              ? `linear-gradient(
                  180deg,
                  ${alpha(theme.palette.primary.main, 0.18)} 0%,
                  ${theme.palette.background.default} 360px
                )`
              : `linear-gradient(
                  180deg,
                  ${alpha(theme.palette.primary.main, 0.12)} 0%,
                  ${theme.palette.background.default} 360px
                )`,
        }}
      >
        <Container maxWidth="lg">
          <Stack spacing={3}>
            {/* ==================================================
                ページヘッダー

                設定ページの名称、ログインユーザーの権限、
                再読み込みボタンを表示する。
            ================================================== */}
            <Paper
              elevation={0}
              sx={{
                p: {
                  xs: 2.5,
                  md: 3.5,
                },
                borderRadius: 4,
                border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
                backgroundColor: alpha(theme.palette.background.paper, 0.86),
                backdropFilter: "blur(12px)",
                boxShadow: `0 18px 45px ${alpha(
                  theme.palette.common.black,
                  0.08,
                )}`,
              }}
            >
              <Stack
                direction={{
                  xs: "column",
                  md: "row",
                }}
                justifyContent="space-between"
                alignItems={{
                  xs: "flex-start",
                  md: "center",
                }}
                spacing={2}
              >
                <Box>
                  {/* ------------------------------------------
                      ページ種別、取得状態、権限状態
                  ------------------------------------------- */}
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    useFlexGap
                    flexWrap="wrap"
                    mb={1}
                  >
                    <Chip
                      label="System Setting"
                      size="small"
                      color="primary"
                      sx={{
                        fontWeight: 700,
                        borderRadius: 999,
                      }}
                    />

                    <Chip
                      label={
                        maintenanceLoading ? "設定読み込み中" : "設定取得済み"
                      }
                      size="small"
                      variant="outlined"
                      sx={{
                        borderRadius: 999,
                        backgroundColor: alpha(
                          theme.palette.background.paper,
                          0.7,
                        ),
                      }}
                    />

                    {/* ログインユーザーの権限を画面上で確認できる */}
                    <Chip
                      label={authorityStatusLabel}
                      size="small"
                      variant="outlined"
                      color={
                        authorityError
                          ? "error"
                          : isAdmin
                            ? "success"
                            : "default"
                      }
                      sx={{
                        borderRadius: 999,
                        fontWeight: 700,
                        backgroundColor: alpha(
                          theme.palette.background.paper,
                          0.7,
                        ),
                      }}
                    />
                  </Stack>

                  {/* ページタイトル */}
                  <Typography
                    variant="h4"
                    component="h1"
                    sx={{
                      fontWeight: 800,
                      letterSpacing: "-0.03em",
                    }}
                  >
                    設定
                  </Typography>

                  {/* ページ説明 */}
                  <Typography
                    sx={{
                      mt: 1,
                      color: "text.secondary",
                      fontSize: {
                        xs: 14,
                        md: 15,
                      },
                      lineHeight: 1.8,
                    }}
                  >
                    ユーザー情報やメンテナンス状態など、
                    システムの運用設定を管理します。
                  </Typography>
                </Box>

                {/* ------------------------------------------
                    設定情報と権限情報を再取得する
                ------------------------------------------- */}
                <Button
                  type="button"
                  variant="contained"
                  onClick={handleReload}
                  disabled={isReloading}
                  sx={{
                    borderRadius: 2,
                    px: 3,
                    py: 1,
                    fontWeight: 700,
                    boxShadow: "none",
                    whiteSpace: "nowrap",
                  }}
                >
                  {isReloading ? "読み込み中" : "再読み込み"}
                </Button>
              </Stack>
            </Paper>

            {/* ==================================================
                メンテナンス情報の取得エラー

                メンテナンスAPIの取得に失敗した場合だけ表示する。
            ================================================== */}
            {maintenanceError && (
              <Alert
                severity="error"
                variant="outlined"
                action={
                  <Button
                    type="button"
                    color="error"
                    size="small"
                    onClick={() => {
                      void getMaintenance();
                    }}
                    disabled={maintenanceLoading}
                  >
                    再試行
                  </Button>
                }
                sx={{
                  borderRadius: 3,
                  backgroundColor: alpha(theme.palette.error.main, 0.04),
                }}
              >
                {maintenanceError}
              </Alert>
            )}

            {/* ==================================================
                権限情報の取得エラー

                権限が取得できない場合は、
                ユーザー管理カードを表示しない。
            ================================================== */}
            {authorityError && (
              <Alert
                severity="warning"
                variant="outlined"
                action={
                  <Button
                    type="button"
                    color="warning"
                    size="small"
                    onClick={() => {
                      void getLoginUserAuthority();
                    }}
                    disabled={authorityLoading}
                  >
                    再試行
                  </Button>
                }
                sx={{
                  borderRadius: 3,
                  backgroundColor: alpha(theme.palette.warning.main, 0.05),
                }}
              >
                {authorityError}
              </Alert>
            )}

            {/* ==================================================
                ユーザー管理

                DBから取得したauthority_idが1の場合だけ表示する。

                管理者以外には、カードも遷移ボタンも
                DOM上に生成されない。
            ================================================== */}
            {isAdmin && (
              <Paper
                elevation={0}
                sx={{
                  overflow: "hidden",
                  borderRadius: 4,
                  border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
                  backgroundColor: theme.palette.background.paper,
                  boxShadow: `0 16px 40px ${alpha(
                    theme.palette.common.black,
                    0.06,
                  )}`,
                }}
              >
                {/* ------------------------------------------
                    ユーザー管理カードの見出し
                ------------------------------------------- */}
                <Box
                  sx={{
                    px: {
                      xs: 2.5,
                      md: 3,
                    },
                    py: 2.25,
                    backgroundColor: alpha(theme.palette.info.main, 0.07),
                  }}
                >
                  <Stack
                    direction={{
                      xs: "column",
                      sm: "row",
                    }}
                    justifyContent="space-between"
                    alignItems={{
                      xs: "flex-start",
                      sm: "center",
                    }}
                    spacing={1}
                  >
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      {/* ユーザー管理アイコン */}
                      <Box
                        sx={{
                          width: 42,
                          height: 42,
                          flexShrink: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          borderRadius: 2.5,
                          color: "info.main",
                          backgroundColor: alpha(theme.palette.info.main, 0.12),
                        }}
                      >
                        <ManageAccountsIcon />
                      </Box>

                      <Box>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 800,
                          }}
                        >
                          ユーザー管理
                        </Typography>

                        <Typography
                          sx={{
                            mt: 0.5,
                            color: "text.secondary",
                            fontSize: 13,
                          }}
                        >
                          ユーザーの追加、権限変更、 利用停止を管理します。
                        </Typography>
                      </Box>
                    </Stack>

                    {/* 管理者にのみ表示されることを示す */}
                    <Chip
                      label="管理者のみ"
                      size="small"
                      variant="outlined"
                      color="success"
                      sx={{
                        borderRadius: 999,
                        fontWeight: 700,
                      }}
                    />
                  </Stack>
                </Box>

                <Divider />

                {/* ------------------------------------------
                    ユーザー管理ページへの遷移部分
                ------------------------------------------- */}
                <Box
                  sx={{
                    p: {
                      xs: 2.5,
                      md: 3,
                    },
                  }}
                >
                  <Stack
                    direction={{
                      xs: "column",
                      md: "row",
                    }}
                    justifyContent="space-between"
                    alignItems={{
                      xs: "stretch",
                      md: "center",
                    }}
                    spacing={2}
                  >
                    <Box>
                      <Typography
                        sx={{
                          fontWeight: 700,
                        }}
                      >
                        登録済みユーザーを管理
                      </Typography>

                      <Typography
                        sx={{
                          mt: 0.75,
                          color: "text.secondary",
                          fontSize: 14,
                          lineHeight: 1.7,
                        }}
                      >
                        新規ユーザーの登録や、 既存ユーザーの権限・利用状態を
                        確認できます。
                      </Typography>
                    </Box>

                    {/* ユーザー管理専用ページへ移動する */}
                    <Button
                      type="button"
                      variant="contained"
                      color="info"
                      endIcon={<ArrowForwardIcon />}
                      onClick={handleOpenUserManagement}
                      sx={{
                        minWidth: 180,
                        borderRadius: 2,
                        px: 3,
                        py: 1.1,
                        fontWeight: 700,
                        boxShadow: "none",
                        whiteSpace: "nowrap",
                      }}
                    >
                      ユーザー管理を開く
                    </Button>
                  </Stack>
                </Box>
              </Paper>
            )}

            {/* ==================================================
                メンテナンス設定

                現在のメンテナンス状態を取得し、
                MaintenanceSettingコンポーネントで編集する。
            ================================================== */}
            <Paper
              elevation={0}
              sx={{
                overflow: "hidden",
                borderRadius: 4,
                border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
                backgroundColor: theme.palette.background.paper,
                boxShadow: `0 16px 40px ${alpha(
                  theme.palette.common.black,
                  0.06,
                )}`,
              }}
            >
              {/* ------------------------------------------
                  メンテナンス設定カードの見出し
              ------------------------------------------- */}
              <Box
                sx={{
                  px: {
                    xs: 2.5,
                    md: 3,
                  },
                  py: 2.25,
                  backgroundColor: alpha(theme.palette.primary.main, 0.05),
                }}
              >
                <Stack
                  direction={{
                    xs: "column",
                    sm: "row",
                  }}
                  justifyContent="space-between"
                  alignItems={{
                    xs: "flex-start",
                    sm: "center",
                  }}
                  spacing={1}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    {/* メンテナンス設定アイコン */}
                    <Box
                      sx={{
                        width: 42,
                        height: 42,
                        flexShrink: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: 2.5,
                        color: "primary.main",
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                      }}
                    >
                      <EngineeringIcon />
                    </Box>

                    <Box>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 800,
                        }}
                      >
                        メンテナンス設定
                      </Typography>

                      <Typography
                        sx={{
                          mt: 0.5,
                          color: "text.secondary",
                          fontSize: 13,
                        }}
                      >
                        利用者への公開状態を 切り替えます。
                      </Typography>
                    </Box>
                  </Stack>
                </Stack>
              </Box>

              <Divider />

              {/* ------------------------------------------
                  メンテナンス情報の状態に応じて表示を切り替える
              ------------------------------------------- */}
              <Box
                sx={{
                  p: {
                    xs: 2.5,
                    md: 3,
                  },
                  minHeight: 240,
                }}
              >
                {maintenanceLoading ? (
                  /**
                   * メンテナンス情報の取得中。
                   */
                  <Box
                    sx={{
                      minHeight: 180,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Loading />
                  </Box>
                ) : maintenanceError ? (
                  /**
                   * メンテナンス情報の取得失敗。
                   */
                  <Box
                    sx={{
                      py: 5,
                      textAlign: "center",
                      color: "text.secondary",
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 700,
                        mb: 1,
                      }}
                    >
                      設定情報を表示できませんでした。
                    </Typography>

                    <Typography
                      sx={{
                        fontSize: 14,
                      }}
                    >
                      再読み込みを実行してください。
                    </Typography>
                  </Box>
                ) : (
                  /**
                   * メンテナンス情報の取得成功。
                   */
                  <MaintenanceSetting />
                )}
              </Box>
            </Paper>
          </Stack>
        </Container>
      </Box>
    </>
  );
};

export default Setting;
